import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { jwtDecode } from 'jwt-decode';

export const useRBAC = (requiredRoles = ["LeadLens AI User"]) => {
    const { isAuthenticated, getAccessTokenSilently } = useAuth0();
    const [roles, setRoles] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const getRoles = async () => {
            if (!isAuthenticated) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                // Get the token that has the roles information
                const token = await getAccessTokenSilently();

                // Decode the JWT token
                const decodedToken = jwtDecode(token);

                // Get the roles from the token
                const roleNamespace = 'https://app.leadlensai.com/oid/roles';
                const userRoles = decodedToken[roleNamespace] || [];

                // For debugging only - remove in production
                console.log('Decoded token roles:', userRoles);

                setRoles(userRoles);
                setError(null);
            } catch (err) {
                console.error('Error getting roles:', err);
                setError(err);
                setRoles([]);
            } finally {
                setIsLoading(false);
            }
        };

        getRoles();
    }, [isAuthenticated, getAccessTokenSilently]);

    // Dev always has access to everything

    const hasRole = roles.includes(RBAC_ROLES.DEV_ONLY) ||
        requiredRoles.some(role => roles.includes(role));

    return { hasRole, isLoading, error, roles };
};

export const RBAC_ROLES = {
    USER: ["LeadLens AI User"],
    ADMIN: ["Admin"],
    ANALYST: ["Analyst"],
    MANAGER: ["Manager"],
    SUPER_ADMIN: ["SuperAdmin"],
    ADMIN_OR_MANAGER: ["Admin", "Manager"],
    ANALYST_OR_ADMIN: ["Analyst", "Admin"],
    ANY_STAFF: ["LeadLens AI User", "Admin", "Analyst", "Manager"],
    DEV_ONLY: ["Development Only"]
};
